import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { DateTime } from 'luxon';
import { getMailProcessing } from '../../information-extraction/api/getMailProcessing.js';
import { MailExtractionResult } from '../../information-extraction/db/MailExtractionResult.js';
import { SapValidationError, validateExtractionForSap } from '../sap-validation.js';
import { sendXML } from '../server/sendXml.js';
import { generateXML } from './generateXmlByMail.js';

export const sendXmlByMail = registerMutation({
  type: () => String,
  operationName: 'sendXmlByMail',
  resolve: (mailID: string, isSentManually?: boolean) => {
    return async () => {
      const mailProcessing = await getMailProcessing(mailID)({
        _id: 1,
        sentToSap: 1,
        extractionResult: MailExtractionResult.allFields,
      });
      if (!mailProcessing) throw new Error('Mail processing not found');
      const extractionResult = mailProcessing.extractionResult;
      if (!extractionResult) throw new Error('No extraction result found for mailID: ' + mailID);

      // Idempotency guard for the automatic path: an offer that was already transmitted must never
      // be auto-sent again — two concurrent extraction runs (e.g. the "Manuell neu starten" escape
      // hatch firing while the original run is still alive) both reach the auto-send and would
      // otherwise place a duplicate order in SAP. A manual resend (isSentManually) stays a
      // deliberate user action and is left untouched.
      if (!isSentManually && mailProcessing.sentToSap?.length) {
        logger.warn(`Automatischer SAP-Versand übersprungen: bereits gesendet (mailID: ${mailID}).`);
        return;
      }

      // Validate the mandatory fields once and persist the outcome immediately, so the mail
      // list / filter reflect it regardless of what happens next (blocked, or successfully sent).
      const report = validateExtractionForSap(extractionResult);
      mailProcessing.sapValidation = report;
      mailProcessing.validatedAt = new Date();
      await mailProcessing.save();
      if (!report.ok) throw new SapValidationError(report);

      // Generate from the same in-memory result we just validated — no refetch, no race
      // with a concurrent edit between validation and generation.
      const xml = await generateXML(extractionResult);
      await sendXML(xml);

      const zeit = DateTime.now().setZone('Europe/Berlin').toFormat('dd.MM.yyyy, HH:mm');
      const meldung = isSentManually
        ? `Manuell an SAP gesendet am ${zeit}`
        : `Automatisch an SAP übertragen am ${zeit}`;
      mailProcessing.updateSentToSap().add(meldung);
      await mailProcessing.save();
    };
  },
  params: [
    { name: 'mailID', type: () => String },
    { name: 'isSentManually', type: () => Boolean },
  ],
});
